import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { InsigniaDeTipo, claseDeArchivo, urlSegura } from './piezas';
import type { Recurso } from './api';

/**
 * VistaPrevia.tsx — la opción 2: ver un recurso sin salir de la biblioteca.
 *
 * ## El título deja de viajar dentro de un `onclick`
 *
 * El vanilla abría esto así:
 *
 *     onclick="PageResources.previewResource('${urlSegura(r.file_url)}','${esc(r.title)}', …)"
 *
 * `esc()` convierte `'` en `&#39;`, pero **el navegador decodifica las
 * entidades del atributo antes de compilar el manejador**. Medido el
 * 2026-09-16, con un recurso idéntico sin apóstrofo como control:
 *
 *     «Guía de O'Brien»  →  manejador no compilado, la vista previa NO se abre
 *     «Manual normal»    →  se abre
 *
 * Y con `x');window.__res=true;//` como título, el código **se ejecutaba** al
 * pulsar «Ver». El propio archivo presumía de que `esc()` «protege al resto de
 * campos»: protege HTML; dentro de una cadena JavaScript mandan otras reglas.
 *
 * ## El título deja de pintarse crudo
 *
 * Y como llegaba decodificado, `previewResource` lo interpolaba sin escapar en
 * cuatro sitios del modal, mientras la tarjeta sí lo escapaba. Un
 * `<img onerror>` en el título se ejecutaba al abrir la vista previa.
 *
 * Aquí el recurso viaja como objeto y React escapa por construcción.
 *
 * ## Sirve también a `#biblioteca/<id>`
 *
 * Por ahí entra la búsqueda global desde el 2026-09-16, así que tiene que poder
 * abrir **cualquier** recurso, no solo los que tienen archivo: un enlace
 * externo se ofrece para abrir en otra pestaña, y uno sin nada enseña su
 * descripción.
 */
export function VistaPrevia({ recurso, onCerrar }: { recurso: Recurso | null; onCerrar: () => void }) {
  const archivo = urlSegura(recurso?.file_url);
  const descarga = urlSegura(recurso?.download_url) ?? archivo;
  const enlace = urlSegura(recurso?.external_url);

  return (
    <Dialog
      abierto={recurso != null}
      onCambio={(v) => !v && onCerrar()}
      titulo={recurso?.title ?? ''}
      descripcion={recurso?.description || undefined}
      ancho="max-w-4xl"
      pie={
        <>
          <Button variant="ghost" onClick={onCerrar}>Cerrar</Button>
          {archivo && descarga && (
            <a
              href={descarga}
              download={recurso?.title}
              className="inline-flex min-h-10 items-center gap-2 rounded-sm bg-brand px-6 text-3
                         font-semibold text-on-brand hover:bg-brand-strong focus-visible:focus-anillo
                         pointer-coarse:min-h-11"
            >
              <i className="bi bi-download" aria-hidden="true" /> Descargar
            </a>
          )}
          {!archivo && enlace && (
            <a
              href={enlace}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex min-h-10 items-center gap-2 rounded-sm bg-brand px-6 text-3
                         font-semibold text-on-brand hover:bg-brand-strong focus-visible:focus-anillo
                         pointer-coarse:min-h-11"
            >
              <i className="bi bi-box-arrow-up-right" aria-hidden="true" /> Abrir enlace
              <span className="sr-only">(se abre en otra pestaña)</span>
            </a>
          )}
        </>
      }
    >
      {recurso && (
        <div className="flex flex-col gap-3">
          <div><InsigniaDeTipo tipo={recurso.resource_type} /></div>
          {archivo ? (
            <Visor url={archivo} titulo={recurso.title} />
          ) : enlace ? (
            <p className="rounded-sm border border-line bg-sunken p-4 text-3 text-ink-2 [overflow-wrap:anywhere]">
              Este recurso es un enlace externo: <span className="text-muted">{enlace}</span>
            </p>
          ) : (
            <p className="rounded-sm border border-line bg-sunken p-4 text-3 text-muted">
              Este recurso no tiene archivo ni enlace adjunto.
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}

/** El visor que le toca a cada clase de archivo. Las URL son del mismo origen
 *  (`/storage/…`), que es lo que permite el `frame-src 'self'` del CSP. */
function Visor({ url, titulo }: { url: string; titulo: string }) {
  const clase = claseDeArchivo(url);
  const marco = 'block w-full rounded-sm border border-line bg-white';

  if (clase === 'imagen') {
    return <img src={url} alt={titulo} className="mx-auto max-h-[70vh] rounded-sm object-contain" />;
  }
  if (clase === 'video') {
    // `preload="metadata"`: que el diálogo no descargue el vídeo entero solo
    // por abrirse.
    return <video src={url} controls preload="metadata" className={`${marco} max-h-[70vh] bg-black`} />;
  }
  if (clase === 'audio') {
    return <audio src={url} controls preload="metadata" className="w-full" />;
  }
  return <iframe src={url} title={titulo} className={`${marco} h-[min(70vh,560px)]`} />;
}
